import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralInvite = ({ basePath = '' }) => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true });
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!code) return;
    axios
      .get(`${API}/campaign/referral/${encodeURIComponent(code)}`)
      .then((r) => {
        // Persist the incoming referral code so the form can attach it
        if (!r.data.expired) sessionStorage.setItem('ff_lokal_ref_in', code.toUpperCase());
        setState({ loading: false, data: r.data });
      })
      .catch(() => setState({ loading: false, error: true }));
  }, [code]);

  // Tick every second for the live 72h countdown
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (state.loading) {
    return (
      <div className="ff-section ff-text-center" data-testid="ff-invite-loading">
        <div className="ff-container">
          <p>Loading your invitation…</p>
        </div>
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="ff-section" data-testid="ff-invite-not-found">
        <div className="ff-container ff-form-shell ff-text-center">
          <h1 className="ff-serif">Invitation Not Found</h1>
          <p>This referral link doesn't seem to exist. You may still qualify for our August offer.</p>
          <Link className="ff-btn ff-btn-primary ff-mt-4" to={`${basePath}/`}>See the Offer</Link>
        </div>
      </div>
    );
  }

  const { referrerFirstName, expired, hoursRemaining, expiresAt } = state.data;
  const pct = Math.round(Number(state.data.discount ?? 15));
  const expiryDate = expiresAt ? new Date(expiresAt) : null;
  const expiryStr = expiryDate ? expiryDate.toLocaleString('en-CA', { timeZone: 'America/Toronto' }) : '';
  const under24 = !expired && hoursRemaining < 24;

  // Live 72h countdown — recompute on every tick.
  const countdown = (() => {
    if (!expiryDate) return null;
    const diff = expiryDate.getTime() - now;
    if (diff <= 0) return { expired: true, h: 0, m: 0, s: 0 };
    const h = Math.floor(diff / (1000 * 60 * 60));
    const m = Math.floor((diff / (1000 * 60)) % 60);
    const s = Math.floor((diff / 1000) % 60);
    return { expired: false, h, m, s };
  })();

  if (expired) {
    return (
      <div className="ff-section" data-testid="ff-invite-expired">
        <div className="ff-container ff-form-shell ff-text-center">
          <h1 className="ff-serif">This referral window has ended</h1>
          <p>
            The referral link from <strong>{referrerFirstName}</strong> has expired,
            but you may still qualify for the current August offer.
          </p>
          <Link className="ff-btn ff-btn-primary ff-mt-4" to={`${basePath}/`} data-testid="ff-invite-see-offer">
            See the August Offer
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="ff-invite">
      {under24 && (
        <div className="ff-referral-banner" data-testid="ff-invite-urgency">
          Your referral offer expires in <strong>less than 24 hours</strong>.
        </div>
      )}
      <section className="ff-hero">
        <div className="ff-container">
          <p className="ff-eyebrow">A personal invitation</p>
          <h1>You Have Been Invited to Save {pct}%</h1>
          <p>
            <strong>{referrerFirstName}</strong> invited you to The Lokal's Corporate & Custom Wine program.
            Choose your wine experience and receive {pct}% off when you submit your qualifying
            order within the referral period.
          </p>
          <p style={{ fontSize: 14, opacity: 0.85, marginBottom: 12 }}>
            Offer expires: <strong>{expiryStr}</strong>
          </p>

          {countdown && !countdown.expired && (
            <div style={{ marginBottom: 24 }}>
              <div
                data-testid="ff-invite-countdown"
                style={{
                  display: 'inline-flex',
                  gap: 12,
                  padding: '14px 20px',
                  background: under24 ? 'rgba(169,64,64,0.25)' : 'rgba(255,255,255,0.08)',
                  border: `1px solid ${under24 ? 'rgba(255,180,180,0.5)' : 'rgba(255,255,255,0.18)'}`,
                  borderRadius: 12,
                  backdropFilter: 'blur(8px)',
                }}
              >
                <span style={{ fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', alignSelf: 'center', opacity: 0.9 }}>
                  Expires in
                </span>
                {[
                  { v: countdown.h, l: 'hrs' },
                  { v: countdown.m, l: 'min' },
                  { v: countdown.s, l: 'sec' },
                ].map((u) => (
                  <div key={u.l} style={{ textAlign: 'center', minWidth: 48 }}>
                    <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>
                      {String(u.v).padStart(2, '0')}
                    </div>
                    <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', opacity: 0.85 }}>
                      {u.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <button
            className="ff-btn ff-btn-gold ff-btn-lg"
            data-testid="ff-invite-claim"
            onClick={() => navigate(`${basePath}/order?ref=${encodeURIComponent(code)}`)}
          >
            Claim My {pct}% Discount
          </button>
        </div>
      </section>

      <section className="ff-section">
        <div className="ff-container ff-form-shell ff-text-center">
          <h2>What happens next?</h2>
          <p className="ff-lede">
            Complete a short form. Our team will confirm your wine selection, pricing, payment, and promotional eligibility.
          </p>
          <button
            className="ff-btn ff-btn-primary ff-btn-lg ff-mt-4"
            data-testid="ff-invite-start"
            onClick={() => navigate(`${basePath}/order?ref=${encodeURIComponent(code)}`)}
          >
            Start My Order
          </button>
          <p style={{ marginTop: 20, fontSize: 13, color: 'var(--ff-brown-soft)' }}>
            You must be 19+ to participate. Please enjoy responsibly.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ReferralInvite;
